// ─── src/components/dashboard/MentorDetailModal.tsx ──────────────────────────
import { useEffect, type FC } from "react";
import { X, ExternalLink, MapPin, Building2, Clock, Monitor, Calendar } from "lucide-react";
import type { Mentor } from "../../types/mentor";
import { avatarColor, initials, formatDate } from "../../utils/formatter";

interface Props {
  mentor:  Mentor;
  onClose: () => void;
}

const Field: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-brand-muted mb-0.5">{label}</p>
    <p className="text-white text-sm">{value ?? "—"}</p>
  </div>
);

const Section: FC<{
  title:    string;
  icon?:    React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div>
    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase
                   tracking-wider text-brand-muted mb-3">
      {icon}{title}
    </h3>
    <div className="bg-brand-navy/50 rounded-xl p-4">{children}</div>
  </div>
);

const FREQ_BADGE: Record<string, string> = {
  "on-demand": "bg-brand-orange/20 text-brand-orange border border-brand-orange/30",
  monthly:     "bg-teal-500/20 text-teal-300 border border-teal-500/30",
  quarterly:   "bg-indigo-500/20 text-indigo-300 border border-indigo-500/30",
};

const MODE_BADGE: Record<string, string> = {
  Online: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  Onsite: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
};

export const MentorDetailModal: FC<Props> = ({ mentor, onClose }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4
                 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto
                      shadow-2xl shadow-black/40 animate-in fade-in-0
                      slide-in-from-bottom-4 duration-200">

        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-brand-border">
          <div className="flex items-center gap-4">
            <div
              className={`${avatarColor(mentor.name)} w-14 h-14 rounded-2xl
                          flex items-center justify-center text-white
                          text-lg font-bold shrink-0`}
            >
              {initials(mentor.name)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{mentor.name}</h2>
              <p className="text-brand-muted text-sm">{mentor.currentRole}</p>
              <p className="text-brand-muted text-xs mt-0.5">{mentor.company}</p>
              {mentor.createdAt && (
                <p className="text-brand-muted text-xs mt-0.5">
                  Registered {formatDate(mentor.createdAt)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-brand-muted hover:text-white
                       hover:bg-brand-border transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Contact */}
          <Section title="Contact Information">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Email"    value={mentor.email} />
              <Field label="Phone"    value={mentor.phone} />
              <Field label="Location" value={mentor.location} />
            </div>
            {mentor.linkedin && (
              <a
                href={mentor.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-orange
                           hover:text-brand-orangeHover text-sm mt-3 transition-colors"
              >
                LinkedIn Profile <ExternalLink size={13} />
              </a>
            )}
          </Section>

          {/* Professional */}
          <Section
            title="Professional Background"
            icon={<Building2 size={14} className="text-brand-orange" />}
          >
            <div className="grid grid-cols-2 gap-4 mb-3">
              <Field label="Company"      value={mentor.company} />
              <Field label="Current Role" value={mentor.currentRole} />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-0.5">Background</p>
              <p className="text-white text-sm leading-relaxed whitespace-pre-line">
                {mentor.professionalBackground ?? "—"}
              </p>
            </div>
          </Section>

          {/* Topics */}
          <Section title="Mentorship Topics">
            <div className="flex flex-wrap gap-2">
              {mentor.topics.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1 rounded-full text-xs font-medium
                             bg-brand-orange/15 text-brand-orange
                             border border-brand-orange/25"
                >
                  {t}
                </span>
              ))}
              {mentor.otherTopic && (
                <span
                  className="px-3 py-1 rounded-full text-xs font-medium
                             bg-brand-border text-brand-muted border border-brand-border"
                >
                  {mentor.otherTopic}
                </span>
              )}
            </div>
          </Section>

          {/* Availability */}
          <Section
            title="Availability"
            icon={<Clock size={14} className="text-brand-orange" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Frequency */}
              <div>
                <p className="text-xs text-brand-muted mb-1.5">Frequency</p>
                <span
                  className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                              ${FREQ_BADGE[mentor.frequency] ?? "bg-gray-500/20 text-gray-300 border border-gray-500/30"}`}
                >
                  {mentor.frequency}
                </span>
              </div>

              {/* Mode */}
              <div>
                <p className="text-xs text-brand-muted mb-1.5">Mode</p>
                <div className="flex flex-wrap gap-1.5">
                  {mentor.mode.map((m) => (
                    <span
                      key={m}
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5
                                  rounded-full text-xs font-medium
                                  ${MODE_BADGE[m] ?? "bg-gray-500/20 text-gray-300 border border-gray-500/30"}`}
                    >
                      <Monitor size={10} />{m}
                    </span>
                  ))}
                </div>
              </div>

              {/* Cities */}
              <div>
                <p className="text-xs text-brand-muted mb-1.5">
                  <MapPin size={10} className="inline mr-1" />Cities
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {mentor.cities.map((c) => (
                    <span
                      key={c}
                      className="inline-flex px-2.5 py-0.5 rounded-full text-xs
                                 font-medium bg-teal-500/15 text-teal-300
                                 border border-teal-500/25"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* Weekend Schedule */}
          {mentor.weekendSchedule && (
            Object.values(mentor.weekendSchedule).some(Boolean)
          ) && (
            <Section
              title="Weekend Availability"
              icon={<Calendar size={14} className="text-brand-orange" />}
            >
              {(
                [
                  { key: "weekend1", label: "Apr 16–18, 2026", subtitle: "Damascus · Aleppo · Latakia" },
                  { key: "weekend2", label: "Apr 23–25, 2026", subtitle: "Hama · Deir ezzur · Daraa"   },
                ] as const
              ).map(({ key, label, subtitle }) => {
                const block = mentor.weekendSchedule?.[key];
                if (!block) return null;
                const DAYS = [
                  { field: "thursday" as const,  label: "Thu" },
                  { field: "friday"   as const,  label: "Fri" },
                  { field: "saturday" as const,  label: "Sat" },
                ] as const;
                const SLOTS = [
                  { key: "morning"   as const, time: "9:00 – 12:00"  },
                  { key: "afternoon" as const, time: "13:00 – 17:00" },
                  { key: "evening"   as const, time: "18:00 – 21:00" },
                ] as const;
                return (
                  <div key={key} className={key === "weekend2" ? "mt-4" : ""}>
                    <p className="text-xs font-semibold text-white mb-0.5">{label}</p>
                    <p className="text-xs text-brand-muted mb-2">{subtitle}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {DAYS.map(({ field, label: dayLabel }) => (
                        <div key={field}>
                          <p className="text-xs text-brand-muted mb-1">{dayLabel}</p>
                          <div className="flex flex-col gap-1">
                            {SLOTS.map(({ key: slot, time }) =>
                              block[field][slot] ? (
                                <span
                                  key={slot}
                                  className="px-2 py-0.5 rounded-md text-xs capitalize
                                             bg-brand-orange/15 text-brand-orange
                                             border border-brand-orange/25"
                                >
                                  {slot} <span className="opacity-60 normal-case">{time}</span>
                                </span>
                              ) : null
                            )}
                            {!SLOTS.some(({ key: s }) => block[field][s]) && (
                              <span className="text-xs text-brand-muted">—</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </Section>
          )}

          {/* Additional Notes */}
          {mentor.additionalNotes && (
            <Section title="Additional Notes">
              <p className="text-white text-sm leading-relaxed whitespace-pre-line">
                {mentor.additionalNotes}
              </p>
            </Section>
          )}

        </div>
      </div>
    </div>
  );
};
